/*
    simple directive wrapper for https://github.com/callmecavs/jump.js

    To jump to an element pass a CSS selector string.
    To scroll from the current position pass a pos or neg number (pixels).
    eg:
    v-scrollto="{target: '#search', options: {duration: 750, offset: -50, a11y: true} }"
    a11y: true - will add tabindex to el and focus on scrollto
    optional noscroll bool param to skip the scroll - based on some parent component object state
  */
/*
    scroll directive - trigger a handler function when window scrolls

    v-scroll="handleScroll"
    then eg:

    methods: {
      handleScroll: function (evt, el) {
        if (window.scrollY > 50) {
          el.classList.add('some-class')
        }
        return window.scrollY > 100
      }
    }
  */
import { defineNuxtPlugin } from 'nuxt/app'
import type { Directive, DirectiveBinding } from 'vue'

export interface JumpOptions {
  duration?: number
  offset?: number
  callback?: () => void
  easing?: (t: number) => number
  a11y?: boolean
}

interface ScrollToBinding {
  target: string | number
  options: JumpOptions
  noscroll?: boolean
}

export type ScrollTo = (target: string | number, options: JumpOptions) => void

// eslint-disable-next-line @typescript-eslint/no-misused-promises -- TODO: Review if async is needed
const scrollto: ScrollTo = async (target, options) => {
  try {
    if (target) {
      if (typeof target === 'string') {
        if (!document.querySelector(target)) {
          // eslint-disable-next-line no-console
          console.warn(`Scroll target not found: ${target}`)
          return
        }
      }
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment -- TODO: Type jump.js properly
      const jump = await import('jump.js')
      // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access -- TODO: Type jump.js properly
      jump?.default(target, options)
    }
  } catch (error) {
    // eslint-disable-next-line no-console
    console.warn('Failed to load jump.js:', error)
  }
}

export default defineNuxtPlugin((nuxtApp) => {
  nuxtApp.provide('puScrollTo', scrollto)

  const scrollToDirective: Directive = {
    mounted(el: HTMLElement, binding: DirectiveBinding<ScrollToBinding>) {
      const handler = () => {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment -- TODO: Type binding properly
        const v = binding.value as any
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access -- TODO: Type binding properly
        const noscroll = typeof v === 'object' && v ? !!v.noscroll : false
        if (noscroll) return
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment -- TODO: Type binding properly
        const target: string | number | undefined =
          typeof v === 'string' || typeof v === 'number'
            ? v
            : // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access -- v is any
              v?.target
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access -- TODO: Type binding properly
        const options = (typeof v === 'object' && v?.options) || {}
        if (typeof target === 'undefined') return
        scrollto(target, options)
      }
      el.addEventListener('click', handler)
      // Store handler for cleanup
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access -- TODO: Type el properly
      ;(el as any)._scrollToHandler = handler
    },
    unmounted(el: HTMLElement) {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access -- TODO: Type el properly
      if (el && (el as any)._scrollToHandler && el.removeEventListener) {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access -- TODO: Type el properly
        el.removeEventListener('click', (el as any)._scrollToHandler)
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access -- TODO: Type el properly
        delete (el as any)._scrollToHandler
      }
    },
  }

  const scrollDirective: Directive = {
    mounted(
      el: HTMLElement,
      binding: DirectiveBinding<(evt: Event, el: HTMLElement) => boolean>,
    ) {
      const handler = (evt: Event) => {
        if (binding.value(evt, el)) {
          window.removeEventListener('scroll', handler)
        }
      }
      window.addEventListener('scroll', handler, { passive: true })
      // Store handler for cleanup
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access -- TODO: Type el properly
      ;(el as any)._windowScrollHandler = handler
    },
    unmounted(el: HTMLElement) {
      if (
        el &&
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access -- TODO: Type el properly
        (el as any)._windowScrollHandler &&
        window &&
        window.removeEventListener
      ) {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access -- TODO: Type el properly
        window.removeEventListener('scroll', (el as any)._windowScrollHandler)
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access -- TODO: Type el properly
        delete (el as any)._windowScrollHandler
      }
    },
  }

  nuxtApp.vueApp.directive('scrollto', scrollToDirective)
  nuxtApp.vueApp.directive('scroll', scrollDirective)
})

// Add type augmentation for TypeScript
declare module '@vue/runtime-core' {
  interface ComponentCustomProperties {
    $puScrollTo: ScrollTo
  }
}
